import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface VersionCheckResult {
  needsReacceptance: string[];
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Polls the legal-docs-version-check edge function to detect new versions
 * of mandatory documents (terms, privacy) that require user re-acceptance.
 *
 * Runs once per session per user; exposes refresh() for manual re-check
 * (e.g. after the user accepts a new version).
 */
export function useLegalDocsVersionCheck(): VersionCheckResult {
  const { user } = useAuth();
  const [needsReacceptance, setNeeds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const check = useCallback(async () => {
    if (!user) {
      setNeeds([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("legal-docs-version-check");
      if (error) return;
      const list = (data as { needs_reacceptance?: string[] })?.needs_reacceptance ?? [];
      setNeeds(list);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { void check(); }, [check]);

  return { needsReacceptance, loading, refresh: check };
}
